/**
 * Allowlist for external redirects requested by the embedded Atlas frame.
 *
 * Billing exists to send a merchant to Shopify to approve a charge. Atlas
 * cannot do that itself — it is an iframe inside warbler inside the Shopify
 * admin — so it posts PARENT_REDIRECT_EXTERNAL and warbler performs the
 * top-level navigation with App Bridge (`shopifyBridge.redirectExternal`).
 *
 * That hands a child frame the ability to move the merchant's entire browser.
 * Without this check the bridge is an open redirector reachable by anything
 * that can post a message the iframe service accepts — a phishing page one
 * click from a merchant who has just been asked to approve a payment.
 *
 * ⚠️ This module is mirrored in atlas at `frontend/src/lib/externalRedirect.ts`.
 * The two repos cannot share code, so they share rules and test cases instead —
 * same arrangement as the two `atlasFeatures.ts` copies. Change one, change
 * both. THIS copy is the authoritative gate: it is the side that navigates,
 * and it must never assume atlas checked first.
 */

/** Shopify store handles are a single label of lowercase alphanumerics and hyphens. */
const SHOP_HANDLE_RE = /^[a-z0-9][a-z0-9-]*$/;

/** The only non-store host billing ever needs to reach. */
const ADMIN_HOST = 'admin.shopify.com';

const MYSHOPIFY_SUFFIX = '.myshopify.com';

/**
 * True when `url` is a Shopify destination safe to hand to a top-level
 * navigation.
 *
 * Accepts exactly the two shapes Shopify's `confirmationUrl` takes:
 *   - `https://admin.shopify.com/...`            (current)
 *   - `https://<handle>.myshopify.com/admin/...` (legacy, still issued)
 *
 * Everything else is rejected, including http, non-empty ports and any
 * userinfo — no legitimate Shopify URL carries those, and each is a way to
 * make a hostile URL read as a friendly one.
 */
export function isAllowedExternalRedirect(url: string): boolean {
  if (typeof url !== 'string' || url.trim() === '') return false;

  let parsed: URL;
  try {
    // No base: a bare path or a protocol-relative URL must NOT resolve against
    // the current page and inherit its origin.
    parsed = new URL(url);
  } catch {
    return false;
  }

  if (parsed.protocol !== 'https:') return false;
  // An explicit port on a Shopify host is always someone's idea, never Shopify's.
  if (parsed.port !== '') return false;
  if (parsed.username !== '' || parsed.password !== '') return false;

  // URL lowercases the hostname for us, but be explicit — this is a security
  // comparison and it should not depend on that.
  const host = parsed.hostname.toLowerCase();

  if (host === ADMIN_HOST) return true;

  if (host.endsWith(MYSHOPIFY_SUFFIX)) {
    const handle = host.slice(0, -MYSHOPIFY_SUFFIX.length);
    // Exact `<handle>.myshopify.com` only. A dot left in the handle means an
    // extra label (a.b.myshopify.com), which is not a store.
    return handle !== '' && !handle.includes('.') && SHOP_HANDLE_RE.test(handle);
  }

  return false;
}
